import { Platform, Text, View, useWindowDimensions, ScrollView, StyleSheet } from 'react-native';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '@stylesheet';
import Modal from 'react-native-modal';
import { useDispatch, useSelector } from 'react-redux';
import LottieView from '@utils/lottieAnimation';
import { useLottieAnim } from '@hooks/lottieHook';
import { Pressable } from 'react-native';
import { Entypo, Ionicons, MaterialIcons } from "@expo/vector-icons";
import FlipCard from 'react-native-flip-card';
import GoogleButtonMail from '../../../TopLevel/Components/Authorisation/GoogleButtonMail';
import MicrosoftButtonMail from '../../../TopLevel/Components/Authorisation/MicrosoftButtonMail';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { HIDE_ALL_MODALS, HIDE_MAIL_MODAL, SHOW_AUTH_MODAL, ADD_INTEGRATION, UPDATE_USER, MAIL_AUTHENTICATED } from '@reduxLocal/actions/types';
import { StyledModal, StyledModalHandle, Title } from '@constants/Themed';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import apis from '@api/api';
import { XStack, YStack } from 'tamagui';
import { useQueryClient } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

/**
 * MailLoginModal component provides a modal interface for users to sign in to their email accounts.
 * 
 * This modal supports authentication via Microsoft and Google OAuth providers, as well as manual SMTP configuration.
 * Users can flip between OAuth sign-in and manual configuration using a FlipCard UI.
 * 
 * Features:
 * - OAuth sign-in with Microsoft and Google (with Gmail modify scope).
 * - Manual SMTP configuration form for custom email providers.
 * - Loading modal feedback during authentication.
 * - Option to make the connected email visible to the company.
 * - Enhanced visual styling with modern UI elements
 * - Improved accessibility and user experience
 * 
 * @remarks
 * Caution: Personal email accounts (such as Gmail or Outlook) have sending limits and are typically free-to-use for low volumes.
 * For higher volume or business-critical use, consider using a dedicated business email provider or transactional email service.
 */

// Create styles dynamically based on theme for better visibility
const getEnhancedStyles = (theme: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  headerContainer: {
    flex: Platform.select({ web: 1.5, default: 1.2 }),
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Platform.select({ web: 20, default: 16 }),
    paddingTop: Platform.select({ web: 20, default: 16 }),
    paddingBottom: Platform.select({ web: 0, default: 16 }),
  },
  titleContainer: {
    alignItems: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: Platform.select({ web: 28, default: 24 }),
    fontWeight: '700',
    color: 'white',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: Platform.select({ web: 16, default: 14 }),
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    lineHeight: Platform.select({ web: 22, default: 20 }),
    paddingHorizontal: 10,
  },
  lottieContainer: {
    width: '100%',
    maxWidth: 200,
    aspectRatio: 1,
    alignSelf: 'center',
    marginVertical: Platform.select({ web: 0, default: 10 }),
  },
  contentContainer: {
    flex: Platform.select({ web: 3, default: 2.5 }),
    paddingHorizontal: Platform.select({ web: 20, default: 16 }),
    paddingBottom: Platform.select({ web: 0, default: 20 }),
    paddingTop: Platform.select({ web: 0, default: 10 }),
  },
  flipCardContainer: {
    flex: 1,
    borderRadius: 16,
    overflow: 'hidden',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    minHeight: Platform.select({ web: 400, default: 300 }),
  },
  oauthContainer: {
    flex: 1,
    borderRadius: 16,
    padding: Platform.select({ web: 24, default: 20 }),
    justifyContent: 'space-between',
    minHeight: Platform.select({ web: 400, default: 300 }),
  },
  oauthButtonsContainer: {
    flex: 1,
    justifyContent: 'center',
    gap: 16,
    paddingVertical: Platform.select({ web: 0, default: 20 }),
  },
  buttonWrapper: {
    borderRadius: 12,
    overflow: 'hidden',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
  dividerText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    marginHorizontal: 16,
    fontWeight: '500',
  },
  manualConfigButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    marginBottom: 10,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  manualConfigButtonText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 16,
  },
  manualFormContainer: {
    flex: 1,
    borderRadius: 16,
    padding: Platform.select({ web: 20, default: 16 }),
    paddingBottom: Platform.select({ web: 20, default: 16 }),
    minHeight: Platform.select({ web: 400, default: 300 }),
    justifyContent: 'space-between',
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
  },
  formTitle: {
    flex: 1,
    fontSize: Platform.select({ web: 20, default: 18 }),
    fontWeight: '600',
    marginLeft: 12,
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 20,
    paddingBottom: Platform.select({ web: 0, default: 10 }),
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.2)',
    minHeight: 70,
    flexShrink: 0,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 12,
    gap: 8,
    minHeight: 50,
  },
  backButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  submitButton: {
    backgroundColor: '#007AFF',
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 5,
  },
  buttonText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 16,
  },
  warningContainer: {
    backgroundColor: 'rgba(255, 193, 7, 0.2)',
    borderLeftWidth: 4,
    borderLeftColor: '#FFC107',
    borderRadius: 8,
    padding: 12,
    marginTop: 10,
    marginBottom: 10,
  },
  warningText: {
    color: '#FFC107',
    fontSize: Platform.select({ web: 12, default: 11 }),
    lineHeight: 16,
    fontWeight: '500',
  },
  formFieldContainer: {
    marginBottom: 16,
  },
  requiredIndicator: {
    color: '#ef4444',
    marginLeft: 4,
  },
});

const MailLoginModal = () => {
  const mailVisible = useSelector((state: DefaultRootState) => state.modals.mailModal);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const theme = themeSelector();
  const [flip, setFlip] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const animation = useLottieAnim();
  const mailModalRef = useRef<StyledModalHandle>(null);
  const manMailRef = useRef<any>(null);
  const prevMailVisibleRef = useRef<boolean>(false);

  // Add state for better UX
  const [isLoading, setIsLoading] = useState(false);

  // Get dynamic styles based on theme
  const enhancedStyles = getEnhancedStyles(theme);

  const _handleLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  // Sync modal with Redux state - this is the single source of truth
  // Improved to handle modal re-opening properly
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    // Only act when state actually changes
    if (mailVisible !== prevMailVisibleRef.current) {
      prevMailVisibleRef.current = mailVisible;

      if (mailVisible) {
      // Open modal and reset state when Redux says to show
        // Use setTimeout to ensure ref is available after component mount/remount
        timeoutId = setTimeout(() => {
          setFlip(false);
          setIsLoading(false);
          if (mailModalRef.current?.openModal) {
            mailModalRef.current.openModal();
          }
        }, 100);
      } else {
        // Close modal and reset state when Redux says to hide
        if (mailModalRef.current?.closeModal) {
          mailModalRef.current.closeModal();
        }
        setFlip(false);
        setIsLoading(false);
      }
    }

    // Cleanup function to clear timeout if component unmounts or effect re-runs
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [mailVisible]);

  return (
    <StyledModal
      title="Connect Your Email Account"
      caption='Enhance your AI experience with personalized email intelligence and automated customer support capabilities'
      ref={mailModalRef}
      customContent={(
        <View style={enhancedStyles.headerContainer}>
          <View style={enhancedStyles.titleContainer}>
            <Title style={enhancedStyles.title}>Email Integration</Title>
            <Text style={enhancedStyles.subtitle}>
              Connect your email to unlock AI-powered customer support and personalized assistance
            </Text>
          </View>
          
          <View style={enhancedStyles.lottieContainer}>
            <LottieView 
              ref={animation} 
              style={{ width: '100%', height: '100%' }} 
              source={require('@assets/animations/95559-send-mail.json')} 
              autoPlay 
              loop={true}
            />
          </View>

          <View style={enhancedStyles.warningContainer}>
            <Text style={enhancedStyles.warningText}>
              💡 Personal email accounts have sending limits. For business use, consider dedicated email services.
            </Text>
          </View>
        </View>
      )}>
      
      {/* Inline loading overlay */}
      {isLoading && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <View style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 20,
            alignItems: 'center',
            gap: 12,
          }}>
            <LottieView 
              style={{ width: 60, height: 60 }} 
              source={require('@assets/animations/spin-leaf-loader.json')} 
              autoPlay 
              loop 
            />
            <Text style={{ color: 'black', fontSize: 16, fontWeight: '600' }}>
              Connecting...
            </Text>
          </View>
        </View>
      )}

      <View style={[enhancedStyles.contentContainer, { minHeight: "80%" }]}>
        <View style={enhancedStyles.flipCardContainer}>
          <FlipCard
            flipHorizontal={true}
            flipVertical={false}
            flip={flip}
            clickable={false}
            style={{ flex: 1 }}
          >
            {/* OAuth Sign-in Front */}
            <View style={[enhancedStyles.oauthContainer, { minHeight: "60%" }]}>
              <View style={enhancedStyles.oauthButtonsContainer}>
                <MicrosoftButtonMail />
                <GoogleButtonMail
                  scopes={['email', 'profile', 'https://www.googleapis.com/auth/gmail.modify']}
                  loading={_handleLoading}
                  callback={() => {
                    // Invalidate mailboxes query if in company context
                    if (currentCompany) {
                      queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
                    }

                    // Close modal after successful Google auth
                    dispatch({ type: HIDE_MAIL_MODAL });
                    setFlip(false);
                    setIsLoading(false);
                  }}
                />
              </View>

              <View>
                <View style={enhancedStyles.dividerContainer}>
                  <View style={enhancedStyles.dividerLine} />
                  <Text style={enhancedStyles.dividerText}>or configure manually</Text>
                  <View style={enhancedStyles.dividerLine} />
                </View>

                <Pressable
                  onPress={() => setFlip(true)}
                  style={[
                    enhancedStyles.manualConfigButton,
                    { opacity: isLoading ? 0.6 : 1, backgroundColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.05)' }
                  ]}
                  disabled={isLoading}
                  accessibilityRole="button"
                  accessibilityLabel="Configure email manually with SMTP settings"
                >
                  <MaterialIcons name="settings" size={20} color={theme ? 'black' : 'white'} />
                  <Text style={enhancedStyles.manualConfigButtonText}>
                    Manual SMTP Configuration
                  </Text>
                  <Ionicons name="arrow-forward" size={16} color={theme ? 'black' : 'white'} />
                </Pressable>
              </View>
            </View>

            {/* Manual Configuration Back */}
            <View style={[enhancedStyles.manualFormContainer, { minHeight: "60%" }]}>
              <View style={{ flex: 1 }}>
                <View style={enhancedStyles.formHeader}>
                  <MaterialIcons name="email" size={24} color={theme ? 'black' : 'white'} />
                  <Text style={[enhancedStyles.formTitle, { color: theme ? 'black' : 'white' }]}>SMTP Configuration</Text>
                </View>



                <ScrollView
                  style={{ flex: 1 }}
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={{
                    paddingBottom: Platform.select({ web: 10, default: 20 }),
                  }}
                  keyboardShouldPersistTaps="handled"
                >
                <GenerateSchemaForm
                  submitFunction={apis.authManMail}
                  ref={manMailRef}
                  scrollEnabled={false}
                  hideButton
                  extraCallBack={async (response) => {
                    setIsLoading(false);

                    // Update local user state with the mail authentication data
                    if (response?.data) {
                      const mailAuthenticated = response.data.mailAuthenticated === true;
                      const mailType = response.data.mailType || 'manual';

                      // Update user authentications with mail authentication flags and data
                      dispatch({
                        type: UPDATE_USER,
                        payload: {
                          authentications: {
                            mail: {
                              mailAuthenticated: mailAuthenticated,
                              mailType: mailType,
                            },
                            manualMail: response.data.manualMail
                          }
                        }
                      });

                      // Also update app-level mail authenticated state for consistency
                      if (mailAuthenticated) {
                        dispatch({ 
                          type: MAIL_AUTHENTICATED, 
                          payload: true 
                        });
                      }

                      // Invalidate and refetch mailboxes if in company context
                      if (currentCompany) {
                        queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
                      }

                      // Show success message
                      dispatch({
                        type: SHOW_AUTH_MODAL,
                        data: {
                          content: response.message || 'Email account connected successfully!',
                          type: 'success'
                        }
                      });

                      // Close modal and reset flip state
                      dispatch({ type: HIDE_MAIL_MODAL });
                      setFlip(false);
                      setIsLoading(false);
                    } else {
                      // Show error if response failed
                      const errorMessage = response?.error || response?.message || 'Failed to connect email account. Please check your SMTP settings.';
                      dispatch({
                        type: SHOW_AUTH_MODAL,
                        data: {
                          content: errorMessage,
                          type: 'error'
                        }
                      });
                    }
                  }}
                  schema={{
                    email: {
                      type: 'string',
                      title: 'Email Address *',
                      format: 'email',
                      required: true,
                      content: { helpText: "Your email address" }
                    },
                    password: {
                      type: 'string',
                      title: 'Password/App Password *',
                      format: 'password',
                      required: true,
                      content: { helpText: "Your email password or app-specific password (use app password for Gmail)" }
                    },
                    smtpHost: {
                      type: 'string',
                      title: 'SMTP Host *',
                      required: true,
                      content: { helpText: "e.g., smtp.gmail.com, smtp.office365.com, smtp.mail.yahoo.com" }
                    },
                    smtpPort: {
                      type: 'number',
                      title: 'SMTP Port *',
                      required: true,
                      content: { helpText: "Usually 587 for TLS, 465 for SSL" }
                    },
                    smtpSecure: {
                      type: 'boolean',
                      title: 'Use Secure Connection (SSL/TLS)',
                      default: true,
                      content: { helpText: "Enable SSL/TLS encryption (required for port 465, recommended for 587)" }
                    },
                    imapHost: {
                      type: 'string',
                      title: 'IMAP Host (Optional)',
                      content: { helpText: "For receiving emails: imap.gmail.com, outlook.office365.com" }
                    },
                    imapPort: {
                      type: 'number',
                      title: 'IMAP Port (Optional)',
                      content: { helpText: "Usually 993 for SSL, 143 for non-SSL" }
                    },
                    visible_to_company: {
                      type: 'boolean',
                      title: 'Visible to Company',
                      default: false,
                      content: { helpText: "Allow your company to see this email account" }
                    }
                  }}
                />
                </ScrollView>
              </View>

              <View style={enhancedStyles.actionButtonsContainer}>
                <Pressable
                  onPress={() => setFlip(false)}
                  style={[enhancedStyles.actionButton, enhancedStyles.backButton, { backgroundColor: theme ? 'grey' : 'white' }]}
                  accessibilityRole="button"
                  accessibilityLabel="Go back to OAuth sign-in options"
                >
                  <Ionicons name="arrow-back" size={20} color={theme ? "white" : "black"} />
                  <Text style={[enhancedStyles.buttonText]}>Back</Text>
                </Pressable>

                <Pressable
                  onPress={() => {
                    setIsLoading(true);
                    manMailRef.current?.submitForm?.();
                  }}
                  style={[
                    enhancedStyles.actionButton, 
                    enhancedStyles.submitButton,
                    { opacity: isLoading ? 0.6 : 1 }
                  ]}
                  disabled={isLoading}
                  accessibilityRole="button"
                  accessibilityLabel="Submit SMTP configuration"
                >
                  {isLoading ? (
                    <>
                      <LottieView 
                        style={{ width: 20, height: 20 }} 
                        source={require('@assets/animations/spin-leaf-loader.json')} 
                        autoPlay 
                        loop 
                      />
                      <Text style={enhancedStyles.buttonText}>Connecting...</Text>
                    </>
                  ) : (
                    <>
                      <Text style={enhancedStyles.buttonText}>Connect</Text>
                        <Ionicons name="checkmark" size={20} color={theme ? "white" : "black"} />
                    </>
                  )}
                </Pressable>
              </View>
            </View>
          </FlipCard>
        </View>
      </View>
    </StyledModal>
  )
}

export default MailLoginModal;